
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MockAPI } from '../services/api';
import { Farm, FarmField, MapFeature, GeometryType, MapFeatureType } from '../types';
import MapComponent from '../components/MapComponent';
import { Button, Card } from '../components/ui';
import { MapPin, Edit, X, Settings, Link as LinkIcon, Plus, Camera, ClipboardCheck, LocateFixed, Route } from 'lucide-react';
import FarmForm from './FarmForm';
import MapFeatureDetailModal from './MapFeatureDetailModal';
import PlacePrimaryPreview from '../components/PlacePrimaryPreview';
import {isPhotoPlace} from '../services/placeModel';
import { useFarm } from '../services/farmContext';
import { useAuth } from '../services/authContext';
import { defaultPropertyGeofence } from '../config/propertyGeofence';
import QuickCapturePhotoSheet from '../components/QuickCapturePhotoSheet';
import type {CapturedPhotoFile} from '../components/PhotoCaptureFlow';
import {useGpsSession} from '../services/GpsSessionContext';
import LogWorkSheet from '../components/LogWorkSheet';
import RecordRouteSheet from '../components/RecordRouteSheet';
import BirdListeningSheet from '../components/BirdListeningSheet';
import {listeningRegion} from '../services/birds/listeningRegion';
import {useBirdListeningSettings} from '../services/birds/settings';
import { liveQuery } from 'dexie';
import { db } from '../services/db';
import {readFieldMapState,saveFieldMapState} from '../services/fieldMapContext';
import {usePresentationHold} from '../services/usePresentation';
import {useRouteData} from '../services/useRouteData';
import {useRouteRecording} from '../services/RouteRecordingContext';
import {Link,useNavigate,useSearchParams,useLocation} from 'react-router-dom';

const FarmMapExplorer: React.FC = () => {
  const { activeFarm, setActiveFarmId } = useFarm();
  const { privileges,user } = useAuth();
  const captureGps=useGpsSession(),nativeCamera=useRef<HTMLInputElement>(null),captureFix=useRef<CapturedPhotoFile['capture']>();
  const nativeCaptureScope=useRef<{account:string;farmId:string}>();
  const [capturedFiles,setCapturedFiles]=useState<CapturedPhotoFile[]>([]);
  useEffect(()=>setCapturedFiles([]),[user?.email,activeFarm?.farm_id]);
  const route=useRouteRecording();
  const navigate=useNavigate(),location=useLocation(),[search,setSearch]=useSearchParams();
  const selectedRoute=search.get('route');
  const selectedRouteData=useRouteData(selectedRoute||undefined);
  const photoRoute=route?.recording?route.active:selectedRouteData.route||(!selectedRoute?route?.active:undefined);
  const routeTarget=photoRoute&&photoRoute.farmId===activeFarm?.farm_id&&(photoRoute.ownerEmail===user?.email||photoRoute.visibility==='property'&&!!photoRoute.endedAt)?{id:photoRoute.id,farmId:photoRoute.farmId,name:photoRoute.name||'Recorded route'}:undefined;
  const canTakePhoto=!!privileges.scouting?.write||!!routeTarget&&!!privileges.communityParticipation?.write;
  useEffect(()=>{route?.selectRoute(selectedRoute);},[selectedRoute]);
  const [farm, setFarm] = useState<Farm | undefined>(activeFarm || undefined);
  const [displayFeatures, setDisplayFeatures] = useState<MapFeature[]>([]);
  const [selectedFeature, setSelectedFeature] = useState<MapFeature | null>(null);
  // Only a deliberate Place round trip restores an inspector and viewport.
  // Sidebar navigation and fresh visits always start with a clean field map.
  const restorePlace=useRef(location.state?.fieldMapIntent==='restore-place').current;
  useEffect(()=>{
    if(!restorePlace)return;
    const {fieldMapIntent,...state}=window.history.state?.usr||{};
    window.history.replaceState({...window.history.state,usr:state},'');
  },[restorePlace]);
  const mapState=useMemo(()=>restorePlace?readFieldMapState(user?.email||'',farm?.farm_id||''):{},[user?.email,farm?.farm_id,restorePlace]);
  const restoreView=useMemo(()=>mapState.center&&mapState.zoom!==undefined?{center:mapState.center,zoom:mapState.zoom}:undefined,[mapState]);
  const hasRouteView=!!selectedRoute||!!route?.selectedRouteId||!!route?.recording;
  const [viewReset,setViewReset]=useState({scope:'',token:0});
  const viewResetToken=viewReset.scope===((user?.email||'')+':'+(farm?.farm_id||''))?viewReset.token:0;
  const lastRouteView=useRef({scope:'',active:false});
  useEffect(()=>{
    const account=user?.email||'',property=farm?.farm_id||'',scope=account+':'+property;
    if(!account||!property)return;
    const newScope=lastRouteView.current.scope!==scope;
    const previous=!newScope&&lastRouteView.current.active;
    lastRouteView.current={scope,active:hasRouteView};
    if(hasRouteView){saveFieldMapState(account,property,{routeView:true});return;}
    if(previous||newScope&&!restorePlace||mapState.routeView){
      saveFieldMapState(account,property,{routeView:false,center:undefined,zoom:undefined,selectedFeatureId:null});
      setViewReset(value=>({scope,token:value.token+1}));
    }
  },[hasRouteView,user?.email,farm?.farm_id,mapState,restorePlace]);
  const [selectionScope,setSelectionScope]=useState('');
  useEffect(()=>{
    const key=user?.email+':'+farm?.farm_id;
    if(!displayFeatures.length||selectionScope===key)return;
    const restored=displayFeatures.find(f=>f.map_feature_id===mapState.selectedFeatureId)||null;
    setSelectedFeature(restored);
    setSelectedEntityDetails(restored?.entity_type==='farm'?farm:restored?{usda_field_number:restored.label,usda_tract_number:restored.label,total_acres:null,description:restored.notes||''}:null);
    setSelectionScope(key);
  },[displayFeatures,mapState,selectionScope,user?.email,farm]);
  useEffect(()=>{if(selectionScope===user?.email+':'+farm?.farm_id)saveFieldMapState(user?.email||'',farm?.farm_id||'',{selectedFeatureId:selectedFeature?.map_feature_id||null});},[selectedFeature,selectionScope,user?.email,farm?.farm_id]);
  
  // Modal States
  const [isFarmFormOpen, setIsFarmFormOpen] = useState(false);
  const [isFeatureDetailOpen, setIsFeatureDetailOpen] = useState(false);
  useEffect(()=>{setIsFeatureDetailOpen(false);},[user?.email,farm?.farm_id]);
  const [isFabMenuOpen, setIsFabMenuOpen] = useState(false);
  const [activeQuickOverlay, setActiveQuickOverlay] = useState<'photo' | 'log' | 'route' | 'birds' | null>(null);
  const [birdDetailId,setBirdDetailId]=useState<number|null>(null);
  const [birdEntry,setBirdEntry]=useState<{scope:string;region?:{latitude:number;longitude:number}}>();
  const birdScope=(user?.email||'')+':'+(activeFarm?.farm_id||'');
  const birdSettings=useBirdListeningSettings(user?.email||'',activeFarm?.farm_id||'');
  const canUseBirdListening=user?.status==='Active'&&!!privileges.map?.view&&!!activeFarm;
  useEffect(()=>{if(activeQuickOverlay==='birds'&&(!canUseBirdListening||birdEntry?.scope!==birdScope))closeQuickActions();},[canUseBirdListening,birdScope,activeQuickOverlay,birdEntry]);
  const canRecordRoute=!!route&&user?.status==='Active'&&!!privileges.gpsTracking?.write;
  const canQuickAct=!!privileges.scouting?.write||!!privileges.projects?.write||canRecordRoute||!!selectedRoute||canUseBirdListening;
  useEffect(()=>{if(!canQuickAct){setIsFabMenuOpen(false);setActiveQuickOverlay(null);}},[canQuickAct]);
  usePresentationHold(isFarmFormOpen||isFeatureDetailOpen||isFabMenuOpen||!!activeQuickOverlay,'Back to the map');
  const [cameraPermissionStatus, setCameraPermissionStatus] = useState<PermissionState | null>(null);
  const [quickToast, setQuickToast] = useState<string | null>(null);
  const uiDepthRef = useRef(0);
  const suppressPopRef = useRef(0);

  const [editingEntityId, setEditingEntityId] = useState<string | undefined>(undefined);

  // Inspector State
  const [selectedEntityDetails, setSelectedEntityDetails] = useState<any>(null);

  const refreshData = async () => {

      const f = activeFarm ? await db.farms.get(activeFarm.farm_id) : await MockAPI.getSingletonFarm();
      if (!f) { setFarm(undefined); setDisplayFeatures([]); return; }

      setFarm(f);


      // 1. Load persisted places first. This keeps map shapes visible even when
      // retired entities (tracts/fields/contracts) are no longer available.
      const annotations = await MockAPI.getMapFeatures(f.farm_id);
      const combinedFeatures: MapFeature[] = [...annotations];

      // 2. Add farm boundary as a virtual feature when no explicit farm place exists.
      const hasFarmOverride = annotations.some((a) => a.entity_type === 'farm' && a.entity_id === f.farm_id);
      if (f.boundary_geojson && !hasFarmOverride) {
          combinedFeatures.push({
              map_feature_id: `virtual-farm-${f.farm_id}`,
              farm_id: f.farm_id,
              entity_type: 'farm',
              feature_type: MapFeatureType.Farm,
              entity_id: f.farm_id,
              geometry_type: GeometryType.Polygon,
              coordinates_geojson: f.boundary_geojson,
              label: 'Farm Boundary',
              color: '#ffffff',
              stroke_weight: 3,
              fill_opacity: 0,
              source: 'manual_draw',
              server_version: 0
          });
      }

      setDisplayFeatures(combinedFeatures);

  };

  useEffect(() => {
    const database = db;
    const subscription = liveQuery(async () => {
      await database.farms.toArray(); await database.mapFeatures.toArray();
      if (database === db) await refreshData();
    }).subscribe({ error: error => console.error('Local map could not load', error) });
    return () => subscription.unsubscribe();
  }, [activeFarm?.farm_id]);

  useEffect(() => {
    let mounted = true;
    const precheckCameraPermission = async () => {
      try {
        if (!navigator.permissions?.query) return;
        const status = await navigator.permissions.query({ name: 'camera' as PermissionName });
        if (!mounted) return;
        setCameraPermissionStatus(status.state);
        status.onchange = () => setCameraPermissionStatus(status.state);
      } catch {
        // Browser may not support camera permission query API.
      }
    };

    precheckCameraPermission();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (activeFarm) {
      setFarm(activeFarm);
    }
  }, [activeFarm]);

  useEffect(() => {
    const onPopState = () => {
      if (suppressPopRef.current > 0) {
        suppressPopRef.current -= 1;
        return;
      }
      // Photo entries belong to the data router. Its shared draft guard decides
      // whether Back commits; do not unmount capture on the raw popstate event.
      if(activeQuickOverlay==='photo')return;
      if(activeQuickOverlay==='birds'&&birdDetailId!==null){
        uiDepthRef.current=Math.max(0,uiDepthRef.current-1);setBirdDetailId(null);return;
      }
      if (activeQuickOverlay) {
        uiDepthRef.current=Math.max(0,uiDepthRef.current-1);
        setActiveQuickOverlay(null);
        return;
      }
      if (isFabMenuOpen) {
        uiDepthRef.current=Math.max(0,uiDepthRef.current-1);
        setIsFabMenuOpen(false);
      }
    };

    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [activeQuickOverlay, isFabMenuOpen,birdDetailId]);

  useEffect(()=>{
    if(activeQuickOverlay==='photo'&&!location.state?.mapPhotoCapture){
      uiDepthRef.current=Math.max(0,uiDepthRef.current-1);setActiveQuickOverlay(null);
    }
  },[location.key]);

  const pushUiState = () => {
    window.history.pushState({ ...window.history.state, farmMapQuickAction: true }, '');
    uiDepthRef.current += 1;
  };

  const popUiState = (levels: number) => {
    const safeLevels = Math.max(0, Math.min(levels, uiDepthRef.current));
    if (safeLevels === 0) return;
    // history.go(-2) emits one popstate, not one event per traversed entry.
    suppressPopRef.current += 1;
    uiDepthRef.current -= safeLevels;
    window.history.go(-safeLevels);
  };

  const toggleFabMenu = () => {
    if (isFabMenuOpen) {
      setIsFabMenuOpen(false);
      popUiState(1);
      return;
    }
    setIsFabMenuOpen(true);
    pushUiState();
  };

  const openQuickOverlay = (overlay: 'photo' | 'log' | 'route' | 'birds') => {
    setBirdDetailId(null);
    if (!isFabMenuOpen) {
      setIsFabMenuOpen(true);
      pushUiState();
    }
    setActiveQuickOverlay(overlay);
    if(overlay==='photo'){
      uiDepthRef.current+=1;
      navigate(location.pathname+location.search+location.hash,{state:{...location.state,mapPhotoCapture:true}});
    }else pushUiState();
  };

  useEffect(()=>{const close=(event:KeyboardEvent)=>{if(event.key==='Escape'&&isFabMenuOpen&&!activeQuickOverlay){setIsFabMenuOpen(false);popUiState(1);}};window.addEventListener('keydown',close);return()=>window.removeEventListener('keydown',close);},[isFabMenuOpen,activeQuickOverlay]);

  const closeQuickActions = () => {
    const levels = (activeQuickOverlay ? 1 : 0) + (isFabMenuOpen ? 1 : 0)+(activeQuickOverlay==='birds'&&birdDetailId!==null?1:0);
    setBirdDetailId(null);
    setActiveQuickOverlay(null);
    setIsFabMenuOpen(false);
    popUiState(levels);
  };

  const requestedRouteDialog=search.get('action')==='record-route';
  useEffect(()=>{
    if(!requestedRouteDialog)return;
    const next=new URLSearchParams(search);next.delete('action');setSearch(next,{replace:true});
    if(canRecordRoute){setActiveQuickOverlay('route');pushUiState();}
  },[requestedRouteDialog,canRecordRoute]);
  useEffect(()=>{if(activeQuickOverlay==='route'&&!canRecordRoute)closeQuickActions();},[activeQuickOverlay,canRecordRoute]);



  const handlePhotoCaptureComplete = (result?: { summary?: string; photoId?: string }) => {
    closeQuickActions();
    if (result?.summary) {
      setQuickToast(result.summary);
      window.setTimeout(() => setQuickToast(null), 2800);
    }
  };

  // When a place is clicked
  const handleFeatureClick = async (feature: MapFeature) => {
      setSelectedFeature(feature);
      setEditingEntityId(undefined); // Reset editing state

      if (feature.entity_type === 'farm') {
          setSelectedEntityDetails(farm);
          return;
      }

      // Field/tract APIs are retired; keep inspector stable with lightweight details
      // sourced from the feature itself instead of calling retired endpoints.
      setSelectedEntityDetails({
          usda_field_number: feature.label,
          usda_tract_number: feature.label,
          total_acres: null,
          description: feature.notes || ''
      });
  };

  const handleEditEntityClick = () => {
      if (!selectedFeature) return;
      if (selectedFeature.entity_type === 'farm') {
          setEditingEntityId(selectedFeature.entity_id);
          setIsFarmFormOpen(true);
      } else {
          setIsFeatureDetailOpen(true);
      }
  };
  
  const getEntityLabel = (type?: string) => {
      if (type === 'farm_field') return 'Field Details';
      if (type === 'farm_tract') return 'Tract Details';
      if (type === 'farm') return 'Farm Details';
      return 'Details';
  }

  const geofenceGeometry = farm?.boundary_geojson || null;
  const gpsTrackingAllowed = !!privileges.gpsTracking?.view;
  const isCaptureModalActive = activeQuickOverlay === 'photo';
  const backgroundControlStateClass = !!activeQuickOverlay
    ? 'opacity-40 saturate-50 pointer-events-none transition-all duration-200'
    : 'opacity-100 saturate-100 pointer-events-auto transition-all duration-200';

  const closeSelectedFeature=()=>{
    setSelectedFeature(null);
    setViewReset(value=>({scope:(user?.email||'')+':'+(farm?.farm_id||''),token:value.token+1}));
  };
  const inspector=selectedFeature ? (
              <Card className="relative bg-white/95 backdrop-blur shadow-xl border-none">
                  {isPhotoPlace(selectedFeature)&&<Link to={'/places/'+encodeURIComponent(selectedFeature.map_feature_id)} state={{mapReturn:location.pathname+location.search}} aria-label={`View Place — ${selectedFeature.label||'Place'}`} className="absolute inset-0 z-10 rounded-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-usda-700 focus-visible:-outline-offset-2" onClick={()=>{saveFieldMapState(user?.email||'',farm?.farm_id||'',{selectedFeatureId:selectedFeature.map_feature_id});window.history.replaceState({...window.history.state,usr:{...location.state,fieldMapIntent:'restore-place'}},'');}}/>}
                  {isPhotoPlace(selectedFeature)&&<div className="rounded-t-lg overflow-hidden"><PlacePrimaryPreview id={selectedFeature.map_feature_id} name={selectedFeature.label||'Place'}/></div>}
                  <div className="p-4">
                      <div className="flex justify-between items-start mb-2">
                          <div className="flex items-center space-x-2">
                             <MapPin className="h-5 w-5 text-usda-600" />
                             <h3 className="font-bold text-gray-900">{selectedFeature.label || 'Unknown Feature'}</h3>
                          </div>
                          <button aria-label="Close selected place" onClick={closeSelectedFeature} className="relative z-20 text-gray-400 hover:text-gray-600">
                              <X className="h-5 w-5" />
                          </button>
                      </div>
                      
                      <div className="text-sm text-gray-600 mb-4">
                          {selectedEntityDetails ? (
                              <div className="space-y-1">
                                  {selectedFeature.entity_type === 'farm_field' && (
                                      <>
                                        <p>Field Number: <strong>{(selectedEntityDetails as FarmField).usda_field_number}</strong></p>
                                        <p>Acres: <strong>{(selectedEntityDetails as FarmField).total_acres}</strong></p>
                                      </>
                                  )}
                                   {selectedFeature.entity_type === 'farm_tract' && (
                                      <>
                                        <p>Tract: <strong>{(selectedEntityDetails as any).usda_tract_number}</strong></p>
                                        <p>Description: {(selectedEntityDetails as any).description}</p>
                                      </>
                                  )}
                              </div>
                          ) : (
                              <div className="italic text-gray-500">
                                  {selectedFeature.feature_type === MapFeatureType.Trail ? 'Trail / Road' : 
                                   selectedFeature.feature_type === MapFeatureType.PointOfInterest ? 'Point of Interest' :
                                   selectedFeature.feature_type === MapFeatureType.Structure ? 'Structure' : 'Map Annotation'}
                              </div>
                          )}
                      </div>

                      <div className="flex space-x-2">
                          {isPhotoPlace(selectedFeature)&&<span className="app-button inline-flex items-center justify-center min-h-9 px-3 py-2 text-sm font-medium rounded-md bg-app-control text-white w-full">View Place</span>}
                          {!isPhotoPlace(selectedFeature)&&privileges.map?.write&&<>
                          {selectedFeature.entity_type && selectedFeature.entity_id ? (
                              <>
                                <Button size="sm" onClick={handleEditEntityClick} className="flex-1">
                                    <Edit className="h-3 w-3 mr-2" /> Edit {getEntityLabel(selectedFeature.entity_type)}
                                </Button>
                                {/* Show style settings for ALL features to allow overrides */}
                                <Button size="sm" variant="outline" onClick={() => setIsFeatureDetailOpen(true)} title="Map Settings">
                                    <Settings className="h-4 w-4" />
                                </Button>
                              </>
                          ) : (
                              <Button size="sm" onClick={() => setIsFeatureDetailOpen(true)} className="w-full">
                                  <LinkIcon className="h-3 w-3 mr-2" /> Link / Edit Style
                              </Button>
                          )}
                          </>}
                      </div>
                  </div>
              </Card>
  ) : null;

  if (!farm) {
      // Empty State / Onboarding
      return (
          <div className="h-full w-full flex items-center justify-center bg-gray-100">
              <div className="text-center">
                  <h2 className="text-xl font-bold text-gray-700 mb-4">Welcome to Sonata Fields</h2>
                  <Button onClick={() => setIsFarmFormOpen(true)}>Create Your Farm</Button>
                  {isFarmFormOpen && (
                      <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-black bg-opacity-50 p-4">
                          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl p-6">
                              <FarmForm onSuccess={() => { setIsFarmFormOpen(false); refreshData(); }} />
                          </div>
                      </div>
                  )}
              </div>
          </div>
      );
  }

  return (
    <div className="relative h-full w-full">
      {/* 1. Map Canvas */}
      <MapComponent
        restoreView={viewResetToken||hasRouteView?undefined:restoreView}
        viewResetToken={viewResetToken}
        onViewChange={view=>saveFieldMapState(user?.email||'',farm?.farm_id||'',hasRouteView?{routeView:true}:{...view,routeView:false})}
        onSelectionClose={!activeQuickOverlay?closeSelectedFeature:undefined}
        features={displayFeatures}
        onFeatureClick={handleFeatureClick}
        selectedFeatureId={selectedFeature?.map_feature_id}
        geofenceGeoJson={geofenceGeometry}
        gpsTrackingAllowed={gpsTrackingAllowed}
        isLocationPanelOpen={false}
        onCloseLocationPanel={closeQuickActions}
      />

      {selectedFeature&&<div className={`field-map-inspector z-[400] ${backgroundControlStateClass}`}>{inspector}</div>}

      {/* 4. Modals */}
      {isFeatureDetailOpen && selectedFeature && privileges.map?.write && (
           <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-black bg-opacity-50 p-4">
               <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden">
                   <MapFeatureDetailModal 
                        feature={selectedFeature}
                        onClose={() => setIsFeatureDetailOpen(false)}
                        onSuccess={() => { 
                            setIsFeatureDetailOpen(false); 
                            refreshData(); 
                            setSelectedFeature(null);
                        }}
                   />
               </div>
           </div>
      )}

      {isFarmFormOpen && (
          <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-black bg-opacity-50 p-4">
              <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
                  <FarmForm 
                    farmId={editingEntityId} 
                    onClose={() => setIsFarmFormOpen(false)} 
                    onSuccess={() => { setIsFarmFormOpen(false); refreshData(); }} 
                  />
              </div>
          </div>
      )}

      {isFabMenuOpen && !activeQuickOverlay && <button aria-label="Close quick actions" onClick={closeQuickActions} className="absolute inset-0 z-[1190] bg-black/10" />}
      {canQuickAct&&<div className={`field-map-actions absolute right-5 z-[1200] flex flex-col items-end gap-3 ${backgroundControlStateClass}`} style={{maxHeight:'calc(100% - max(1.5rem, env(safe-area-inset-bottom)) - 1rem)'}}>
        {isFabMenuOpen && !activeQuickOverlay && <div id="field-quick-actions" className="min-h-0 overflow-y-auto bg-white rounded-2xl border border-gray-200 shadow-2xl p-2 w-52 space-y-1">
          {canTakePhoto && <button onClick={()=>{setCapturedFiles([]);nativeCaptureScope.current={account:user?.email||'',farmId:activeFarm?.farm_id||''};openQuickOverlay('photo');const now=Date.now(),point=captureGps.state.position;captureFix.current=undefined;if(matchMedia('(pointer: coarse)').matches){captureFix.current={capturedAt:new Date(now).toISOString()};if(captureGps.allowed&&captureGps.enabled&&captureGps.state.permission==='granted'&&point&&now-point.timestamp<=60000&&point.timestamp<=now+10000)captureFix.current.location={latitude:point.coords.latitude,longitude:point.coords.longitude,measuredAt:point.timestamp};nativeCamera.current?.click();}}} className="w-full rounded-xl p-4 text-left flex gap-3 items-center font-medium hover:bg-usda-50 active:bg-usda-100"><Camera size={22} className="text-usda-700"/>Add photo</button>}
          {privileges.projects?.write && <button onClick={()=>openQuickOverlay('log')} className="w-full rounded-xl p-4 text-left flex gap-3 items-center font-medium hover:bg-usda-50 active:bg-usda-100"><ClipboardCheck size={22} className="text-usda-700"/>Log work</button>}
          {canUseBirdListening&&<button onClick={()=>{setBirdEntry({scope:birdScope,region:listeningRegion(captureGps)});openQuickOverlay('birds');}} className="w-full rounded-xl p-4 text-left flex gap-3 items-center font-medium hover:bg-usda-50"><span aria-hidden="true" className="text-usda-700 text-xl">♪</span>Listen for birds</button>}
          {canRecordRoute&&<><button onClick={()=>{if(!route.active){openQuickOverlay('route');return;}closeQuickActions();void(route.recording?route.pause():route.start());}} className="w-full rounded-xl p-4 text-left flex gap-3 items-center font-medium hover:bg-usda-50"><Route size={22} className="text-usda-700"/>{route.recording?'Pause route':route.active?'Resume route':'Record route'}</button>{route.active&&<button onClick={()=>{closeQuickActions();void route.stop().then(id=>{if(id)navigate('/routes/'+id);});}} className="w-full rounded-xl p-4 text-left font-medium hover:bg-usda-50">Finish route</button>}</>}
          {selectedRoute&&<button onClick={()=>{route?.selectRoute(null);setSearch({});closeQuickActions();}} className="w-full rounded-xl p-4 text-left hover:bg-usda-50">Close saved route</button>}
        </div>}
        <button onClick={toggleFabMenu} aria-label={isFabMenuOpen?'Close actions':route?.recording?'Open map actions — recording route':'Open map actions'} aria-expanded={isFabMenuOpen} aria-controls="field-quick-actions" className="relative shrink-0 h-14 w-14 rounded-full shadow-xl border border-white/40 bg-usda-700 text-white flex items-center justify-center transition-transform duration-150 active:scale-90 focus-visible:ring-4 focus-visible:ring-usda-200">
          {route?.recording&&<span aria-hidden="true" className="absolute -top-1 -left-1 h-3 w-3 rounded-full bg-emerald-400 border-2 border-white"/>}
          <Plus className={`h-6 w-6 transition-transform duration-150 ${isFabMenuOpen?'rotate-45':''}`}/>
        </button>
      </div>}
      {route?.error&&<div role="alert" className="absolute bottom-24 right-5 z-[1200] max-w-xs rounded-md bg-white px-3 py-2 text-sm text-amber-800 shadow">{route.error}</div>}

      {isCaptureModalActive && <div className="fixed inset-0 z-[1250]" aria-hidden="true" />}
      <input ref={nativeCamera} type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif" capture="environment" className="hidden" onChange={event=>{const file=event.target.files?.[0];event.target.value='';if(!file||!user||nativeCaptureScope.current?.account!==user.email||nativeCaptureScope.current?.farmId!==activeFarm?.farm_id)return;const now=Date.now(),fix=captureFix.current?.location;setCapturedFiles([{file,account:user.email,capture:{capturedAt:new Date(now).toISOString(),...(fix&&Math.abs(now-fix.measuredAt)<=120000?{location:fix}:{})}}]);}}/>

      {activeQuickOverlay === 'photo' && (
        <QuickCapturePhotoSheet
          key={capturedFiles.length?capturedFiles[0].file.name+capturedFiles[0].file.lastModified:'capture-empty'}
          onCancel={closeQuickActions}
          onNavigate={()=>{setActiveQuickOverlay(null);setIsFabMenuOpen(false);uiDepthRef.current=0;}}
          onComplete={handlePhotoCaptureComplete}
          preflightCameraPermission={cameraPermissionStatus}
          initialMapFeatureId={selectedFeature&&isPhotoPlace(selectedFeature)?selectedFeature.map_feature_id:''}
          routeTarget={routeTarget}
          initialFiles={capturedFiles}
          initialFarmId={activeFarm?.farm_id}
        />
      )}

      {quickToast && (
        <div className="fixed bottom-6 right-6 z-[1500] bg-emerald-600 text-white text-sm font-medium px-4 py-3 rounded-lg shadow-xl">
          {quickToast}
        </div>
      )}

      {activeQuickOverlay === 'log' && (
        <LogWorkSheet onClose={closeQuickActions} />
      )}
      {activeQuickOverlay === 'route' && canRecordRoute && <RecordRouteSheet onClose={closeQuickActions}/>}
      {activeQuickOverlay === 'birds' && canUseBirdListening && birdEntry?.scope===birdScope && <BirdListeningSheet onClose={closeQuickActions} region={birdEntry.region} settings={birdSettings} selectedBirdId={birdDetailId} onSelectBird={id=>{if(id===null){setBirdDetailId(null);popUiState(1);}else {setBirdDetailId(id);if(birdDetailId===null)pushUiState();}}}/>}
      
    </div>
  );
};

export default FarmMapExplorer;
